import { useState, useEffect } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Bike } from 'models/shared';
import { UserDocument, UserSizes } from 'models/user';
import { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { deserializeBike, serializeBike } from '@util/parseBikeName';
import { isMobile } from '@util/index';
import { FacetCounts } from 'models/product';

type UseMySizesGarageProps = {
  userDoc: UserDocument | null | undefined;
  facet_counts: FacetCounts | undefined;
  handleBikeSelection: (args: {
    bike?: Bike | null;
    remove?: boolean;
    bikes?: Bike[];
  }) => void;
  filterState: CategoryPageSideFilters;
  handleSizeSelection: (sizes: UserSizes) => void;
  bikePendingFilters?: {
    bikeTypes: string[] | undefined;
    bikeModels: string[] | undefined;
    bikeYears: string[] | undefined;
    bikeMakes: string[] | undefined;
  };
  sizePendingFilters?: {
    letterSizes: string[] | undefined;
    numberSizes: string[] | undefined;
  };
};

export const useMySizesGarage = ({
  userDoc,
  facet_counts,
  handleBikeSelection,
  filterState,
  handleSizeSelection,
  bikePendingFilters = {
    bikeTypes: undefined,
    bikeModels: undefined,
    bikeYears: undefined,
    bikeMakes: undefined,
  },
  sizePendingFilters = {
    letterSizes: undefined,
    numberSizes: undefined,
  },
}: UseMySizesGarageProps) => {
  const searchParams = useSearchParams() ?? new URLSearchParams();
  const [isMySizesEnabled, setIsMySizesEnabled] = useState(false);
  const [isMyGarageEnabled, setIsMyGarageEnabled] = useState(false);
  const [selectedBikes, setSelectedBikes] = useState<Bike[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<UserSizes | null>(null);
  const [isEditGarageModalOpen, setIsEditGarageModalOpen] = useState(false);
  const [isMobileEditGarageModalOpen, setIsMobileEditGarageModalOpen] =
    useState(false);
  const [isMobileEditSizesModalOpen, setIsMobileEditSizesModalOpen] =
    useState(false);
  const [isEditSizesModalOpen, setIsEditSizesModalOpen] = useState(false);
  const [isAddBikeModalOpen, setIsAddBikeModalOpen] = useState(false);
  const [topFilter, setTopFilter] = useState<'gear' | 'parts'>('gear');
  const [shouldShowBothFilters, setShouldShowBothFilters] = useState(false);
  const bikesParam = searchParams.get('bikes') ?? '';
  const pathname = usePathname();
  const router = useRouter();

  const getFilterStateBikes = (): Bike[] => {
    if (bikesParam) {
      const parsedBikes = bikesParam
        .split(',')
        .map(deserializeBike)
        .filter((b): b is Bike => b !== null);
      return parsedBikes;
    } else return [];
  };

  const filterStateBikes = getFilterStateBikes();
  const isParts =
    filterStateBikes.length ||
    facet_counts?.category?.[0]?.value?.toLowerCase().includes('parts');
  const filterVariant: 'gear' | 'parts' = isParts ? 'parts' : 'gear';

  const onAddBikeClick = () => {
    setIsEditGarageModalOpen(false);
    setIsAddBikeModalOpen(true);
  };

  const onSyncSelectedBikes = (bikes: Bike[]) => {
    setSelectedBikes(bikes);
    const newBikesParam = bikes.map(serializeBike).join(',');
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    if (newBikesParam) {
      params.set('bikes', newBikesParam);
    } else {
      params.delete('bikes');
    }
    router.push(`${pathname}?${params.toString()}`);
    setIsMyGarageEnabled(true);
    setIsEditGarageModalOpen(false);
    setIsAddBikeModalOpen(false);
    handleBikeSelection({ bikes });
  };

  const onSelectSize = (size: keyof UserSizes) => {
    const newSelectedSizes = {
      ...selectedSizes,
      [size]: !selectedSizes?.[size],
    };
    setSelectedSizes(newSelectedSizes);

    const parsedSizes = Object.entries(newSelectedSizes).reduce<
      Record<string, UserSizes[keyof UserSizes]>
    >((acc, [key, enabled]) => {
      const value = userDoc?.sizes?.[key as keyof UserSizes];
      if (enabled && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const filteredSizes = Object.fromEntries(
      Object.entries(parsedSizes).filter(([, v]) => v !== undefined)
    ) as UserSizes;

    handleSizeSelection(filteredSizes);
  };

  useEffect(() => {
    const bikes: Bike[] = [];
    const years =
      isMobile() && bikePendingFilters?.bikeYears?.length
        ? bikePendingFilters.bikeYears
        : filterState.bikeYears || [];
    const makes =
      isMobile() && bikePendingFilters?.bikeMakes?.length
        ? bikePendingFilters.bikeMakes
        : filterState.bikeMakes || [];
    const models =
      isMobile() && bikePendingFilters?.bikeModels?.length
        ? bikePendingFilters.bikeModels
        : filterState.bikeModels || [];

    for (
      let i = 0;
      i < Math.min(years.length, makes.length, models.length);
      i++
    ) {
      bikes.push({
        year: Number(years[i]),
        make: makes[i] as Bike['make'],
        model: models[i],
      });
    }
    setSelectedBikes(bikes);
    if (bikes.length > 0 && !isMyGarageEnabled) {
      setIsMyGarageEnabled(true);
    }

    const letterSizes =
      isMobile() && sizePendingFilters?.letterSizes?.length
        ? sizePendingFilters.letterSizes
        : filterState.letterSizes || [];
    const numberSizes =
      isMobile() && sizePendingFilters?.numberSizes?.length
        ? sizePendingFilters.numberSizes
        : filterState.numberSizes || [];

    const selected: Record<string, boolean> = {};
    if (userDoc?.sizes) {
      Object.entries(userDoc.sizes).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          selected[key] =
            letterSizes.includes(String(value)) ||
            numberSizes.includes(String(value));
        } else {
          selected[key] = false;
        }
      });
    }
    setSelectedSizes(selected as UserSizes);
    if (Object.values(selected).some(Boolean) && !isMySizesEnabled) {
      setIsMySizesEnabled(true);
    }
  }, [
    filterState.bikeYears,
    filterState.bikeMakes,
    filterState.bikeModels,
    filterState.letterSizes,
    filterState.numberSizes,
    bikePendingFilters?.bikeYears,
    bikePendingFilters?.bikeMakes,
    bikePendingFilters?.bikeModels,
    sizePendingFilters?.letterSizes,
    sizePendingFilters?.numberSizes,
    userDoc?.sizes,
    isMyGarageEnabled,
    isMySizesEnabled,
  ]);

  useEffect(() => {
    if (!pathname?.includes('search')) {
      if (
        ['/shop/recent', '/search/on-sale'].includes(pathname ?? '') ||
        pathname?.startsWith('/profile')
      ) {
        setShouldShowBothFilters(true);
      }
    } else {
      const partsCount = facet_counts?.category?.find(
        (c) => c.value === 'Bike Parts'
      )?.count;
      const gearCount = facet_counts?.category?.find(
        (c) => c.value === 'Riding Gear'
      )?.count;

      if ((partsCount ?? 0) > 0 && (gearCount ?? 0) > 0) {
        if ((gearCount ?? 0) > (partsCount ?? 0)) setTopFilter('gear');
        else setTopFilter('parts');

        setShouldShowBothFilters(true);
      } else setShouldShowBothFilters(false);
    }
  }, [facet_counts?.category, pathname]);

  return {
    isMySizesEnabled,
    setIsMySizesEnabled,
    isMyGarageEnabled,
    setIsMyGarageEnabled,
    selectedBikes,
    setSelectedBikes,
    selectedSizes,
    isEditGarageModalOpen,
    setIsEditGarageModalOpen,
    isMobileEditGarageModalOpen,
    setIsMobileEditGarageModalOpen,
    isMobileEditSizesModalOpen,
    setIsMobileEditSizesModalOpen,
    isEditSizesModalOpen,
    setIsEditSizesModalOpen,
    isAddBikeModalOpen,
    setIsAddBikeModalOpen,
    topFilter,
    shouldShowBothFilters,
    filterVariant,
    onAddBikeClick,
    onSyncSelectedBikes,
    onSelectSize,
  };
};
